import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsApi } from '../../services/apiService';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import Pagination from '../../components/Pagination/Pagination';
import { useCurrency } from '../../context/CurrencyContext';
import './ProjectManagePage.css';
import { PRODUCT_PLACEHOLDER } from '../../utils/placeholders';
import '../../components/AdminCreateModal.css';

const ProjectManagePage = () => {
  const navigate = useNavigate();
  const { selectedCurrency, currencies } = useCurrency();
  
  // Data states
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedType, setSelectedType] = useState('');
  
  // Selection states
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Action dropdown state
  const [actionDropdown, setActionDropdown] = useState({ show: false, projectId: null });

  // Notes modal state
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesProject, setNotesProject] = useState(null);
  const [projectNotes, setProjectNotes] = useState('');

  // Create modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createErrors, setCreateErrors] = useState({});
  const [createForm, setCreateForm] = useState({
    title: '', description: '', projectType: '', budget: '', currency: selectedCurrency.code,
    country: '', city: '', address: '', companyName: '',
    contactName: '', contactEmail: '', contactPhone: '', status: 'published'
  });

  const resetCreateForm = () => {
    setCreateForm({ title: '', description: '', projectType: '', budget: '', currency: selectedCurrency.code, country: '', city: '', address: '', companyName: '', contactName: '', contactEmail: '', contactPhone: '', status: 'published' });
    setCreateErrors({});
  };

  const handleCreateProject = async () => {
    const errors = {};
    if (!createForm.title.trim()) errors.title = 'Project title is required';
    if (Object.keys(errors).length > 0) { setCreateErrors(errors); return; }
    try {
      setCreateSaving(true);
      await projectsApi.adminCreate(createForm);
      setShowCreateModal(false);
      resetCreateForm();
      fetchProjects();
    } catch (err) {
      setCreateErrors({ submit: err.response?.data?.error || 'Failed to create project' });
    } finally {
      setCreateSaving(false);
    }
  };

  // Fetch projects from API
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await projectsApi.getAll({ limit: 200, status: 'all' });
      const projectData = Array.isArray(response) ? response : (response.projects || []);
      setProjects(projectData);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Get unique values for filters
  const uniqueCountries = [...new Set(projects.map(p => p.country).filter(Boolean))];
  const uniqueTypes = [...new Set(projects.map(p => p.projectType).filter(Boolean))];

  // Filter projects
  const filteredProjects = projects.filter(project => {
    const matchesSearch = !searchQuery || 
      project.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.contactEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.country?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.city?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCountry = !selectedCountry || project.country?.toLowerCase() === selectedCountry.toLowerCase();
    const matchesStatus = !selectedStatus || project.status?.toLowerCase() === selectedStatus.toLowerCase();
    const matchesType = !selectedType || project.projectType?.toLowerCase() === selectedType.toLowerCase();
    return matchesSearch && matchesCountry && matchesStatus && matchesType;
  });

  // Pagination
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProjects = filteredProjects.slice(startIndex, startIndex + itemsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCountry, selectedStatus, selectedType]);

  // Handle select item
  const handleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // Handle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(paginatedProjects.map(p => p.id));
    } else {
      setSelectedItems([]);
    }
  };

  // Handle status change
  const handleStatusChange = async (projectId, newStatus) => {
    try {
      await projectsApi.updateStatus(projectId, newStatus);
      fetchProjects();
      setActionDropdown({ show: false, projectId: null });
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  // Handle delete project
  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await projectsApi.delete(projectId);
        fetchProjects();
        setActionDropdown({ show: false, projectId: null });
      } catch (err) {
        console.error('Error deleting project:', err);
        alert('Failed to delete project');
      }
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      alert('Please select projects to delete');
      return;
    }
    if (window.confirm(`Are you sure you want to delete ${selectedItems.length} projects?`)) {
      try {
        await projectsApi.bulkDelete(selectedItems);
        setSelectedItems([]);
        fetchProjects();
      } catch (err) {
        console.error('Error bulk deleting:', err);
        alert('Failed to delete projects');
      }
    }
  };

  // Handle bulk status update
  const handleBulkStatusUpdate = async (status) => {
    if (selectedItems.length === 0) {
      alert('Please select projects to update');
      return;
    }
    try {
      await projectsApi.bulkUpdateStatus(selectedItems, status);
      setSelectedItems([]);
      fetchProjects();
    } catch (err) {
      console.error('Error bulk updating:', err);
      alert('Failed to update projects');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    });
  };

  // Format budget
  const formatBudget = (budget, currency) => {
    if (!budget) return 'N/A';
    const cur = currency || selectedCurrency.code;
    if (budget >= 1000000) return `${cur} ${(budget / 1000000).toFixed(1)}M`;
    if (budget >= 1000) return `${cur} ${(budget / 1000).toFixed(0)}K`;
    return `${cur} ${budget}`;
  };

  // Handle notes modal
  const handleNotesModal = (project) => {
    setActionDropdown({ show: false, projectId: null });
    setNotesProject(project);
    setProjectNotes(project.description || '');
    setShowNotesModal(true);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.pm-action-dropdown')) {
        setActionDropdown({ show: false, projectId: null });
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <AdminLayout>
      <div className="admin-project-manage-content">
        <div className="pm-page-header">
          <div className="pm-page-title-section">
            <h1 className="pm-page-title">Project Management</h1>
            <p className="pm-page-description">Oversee, approve, and organize all project listings across the platform</p>
          </div>
          <div className="pm-header-actions">
            <button className="admin-add-new-btn" onClick={() => { resetCreateForm(); setShowCreateModal(true); }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>
              Add Project
            </button>
            {selectedItems.length > 0 && (
              <>
                <button className="pm-bulk-btn pm-bulk-active" onClick={() => handleBulkStatusUpdate('active')}>
                  Activate ({selectedItems.length})
                </button>
                <button className="pm-bulk-btn pm-bulk-hide" onClick={() => handleBulkStatusUpdate('hidden')}>
                  Hide ({selectedItems.length})
                </button>
                <button className="pm-bulk-btn pm-bulk-delete" onClick={handleBulkDelete}>
                  Delete ({selectedItems.length})
                </button>
              </>
            )}
          </div>
        </div>

        {/* Project Lists Header */}
        <div className="pm-list-header">
          <h2 className="pm-list-title">Project Lists</h2>
          <div className="pm-list-controls">
            <div className="pm-search-box">
              <input 
                type="text" 
                placeholder="Search..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="9.16667" cy="9.16667" r="5.83333" stroke="#969696" strokeWidth="1.5"/>
                <path d="M14.166 14.1665L17.4993 17.4998" stroke="#969696" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            
            {/* Country Filter */}
            <div className="pm-select-wrapper">
              <select 
                className="pm-filter-select"
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
              >
                <option value="">Country</option>
                {uniqueCountries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            
            {/* Status Filter */}
            <div className="pm-select-wrapper">
              <select 
                className="pm-filter-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">Status</option>
                <option value="draft">Draft</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="published">Published</option>
                <option value="unpublished">Unpublished</option>
                <option value="rejected">Rejected</option>
                <option value="archived">Archived</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            
            {/* Type Filter */}
            <div className="pm-select-wrapper pm-category-select">
              <svg className="pm-filter-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.0171 3.86955C17.9209 3.64729 17.7615 3.45822 17.5587 3.32584C17.3559 3.19347 17.1186 3.12363 16.8764 3.12502H3.12644C2.88452 3.1255 2.64792 3.19617 2.44537 3.32846C2.24282 3.46076 2.08301 3.64899 1.98532 3.87032C1.88764 4.09165 1.85629 4.33658 1.89507 4.57538C1.93385 4.81418 2.04109 5.03659 2.20379 5.21565L2.21004 5.22268L7.50144 10.8727V16.875C7.50139 17.1013 7.56274 17.3233 7.67894 17.5174C7.79514 17.7115 7.96185 17.8704 8.16128 17.9772C8.36071 18.084 8.58539 18.1347 8.81136 18.1239C9.03733 18.113 9.25613 18.0411 9.44441 17.9156L11.9444 16.2485C12.1158 16.1343 12.2563 15.9796 12.3535 15.798C12.4507 15.6165 12.5015 15.4137 12.5014 15.2078V10.8727L17.7936 5.22268L17.7999 5.21565C17.9643 5.03741 18.0726 4.8147 18.1112 4.57531C18.1498 4.33592 18.1171 4.09045 18.0171 3.86955Z" fill="#333333"/>
              </svg>
              <select 
                className="pm-filter-select with-icon"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
              >
                <option value="">Filter By Type</option>
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
                <option value="industrial">Industrial</option>
                <option value="infrastructure">Infrastructure</option>
                {uniqueTypes.filter(t => !['residential', 'commercial', 'industrial', 'infrastructure'].includes(t.toLowerCase())).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* White Content Area */}
        <div className="pm-content">
          {loading ? (
            <div className="pm-loading">Loading projects...</div>
          ) : error ? (
            <div className="pm-error">{error} <button onClick={fetchProjects}>Retry</button></div>
          ) : (
          <div className="pm-table">
            <div className="pm-table-header">
              <div className="pm-th pm-th-checkbox">
                <input 
                  type="checkbox" 
                  checked={paginatedProjects.length > 0 && selectedItems.length === paginatedProjects.length}
                  onChange={handleSelectAll}
                />
                <span>All</span>
              </div>
              <div className="pm-th">Image</div>
              <div className="pm-th">Creation date</div>
              <div className="pm-th">Project Title</div>
              <div className="pm-th">Project Type</div>
              <div className="pm-th">Sub Category</div>
              <div className="pm-th">Location</div>
              <div className="pm-th">Company</div>
              <div className="pm-th">Budget</div>
              <div className="pm-th">Deadline</div>
              <div className="pm-th">Views</div>
              <div className="pm-th">Status</div>
              <div className="pm-th">Details</div>
              <div className="pm-th">Action</div>
            </div>
            <div className="pm-table-body">
              {paginatedProjects.length > 0 ? (
                paginatedProjects.map((project) => (
                  <div key={project.id} className="pm-table-row">
                    <div className="pm-td pm-td-checkbox">
                      <input 
                        type="checkbox" 
                        checked={selectedItems.includes(project.id)}
                        onChange={() => handleSelectItem(project.id)}
                      />
                    </div>
                    <div className="pm-td pm-td-image">
                      {project.image ? (
                        <img 
                          src={project.image} 
                          alt={project.title}
                          onError={(e) => { e.target.src = PRODUCT_PLACEHOLDER; }}
                        />
                      ) : (
                        <div className="pm-logo-placeholder">
                          {project.title?.charAt(0) || 'P'}
                        </div>
                      )}
                    </div>
                    <div className="pm-td pm-td-date">{formatDate(project.createdAt)}</div>
                    <div className="pm-td pm-td-name" title={project.title || 'N/A'}>{project.title || 'N/A'}</div>
                    <div className="pm-td pm-td-type">{project.projectType || 'N/A'}</div>
                    <div className="pm-td pm-td-subcat">{project.subCategory || '-'}</div>
                    <div className="pm-td pm-td-location">{project.country || project.city || 'N/A'}</div>
                    <div className="pm-td pm-td-company">{project.companyName || 'N/A'}</div>
                    <div className="pm-td pm-td-budget">{formatBudget(project.budget, project.currency)}</div>
                    <div className="pm-td pm-td-deadline">{formatDate(project.endDate)}</div>
                    <div className="pm-td pm-td-views">{project.views || 0}</div>
                    <div className="pm-td pm-td-groups">
                      <span className={`pm-status-badge ${(project.status || 'draft').toLowerCase()}`}>
                        {({
                          draft: 'Draft',
                          pending_approval: 'Pending',
                          approved: 'Approved',
                          published: 'Published',
                          unpublished: 'Unpublished',
                          rejected: 'Rejected',
                          archived: 'Archived',
                          completed: 'Completed'
                        })[project.status] || project.status || 'Draft'}
                      </span>
                    </div>
                    <div className="pm-td pm-td-details">
                      <button className="pm-details-btn" onClick={() => navigate(`/admin/projects/${project.id}`)}>Details</button>
                    </div>
                    <div className="pm-td pm-td-action">
                      <div className="pm-action-dropdown">
                        <button 
                          className="pm-more-btn"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setActionDropdown({ 
                              show: actionDropdown.projectId === project.id ? !actionDropdown.show : true, 
                              projectId: project.id 
                            }); 
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="5" r="2" fill="#6D6D6D"/>
                            <circle cx="12" cy="12" r="2" fill="#6D6D6D"/>
                            <circle cx="12" cy="19" r="2" fill="#6D6D6D"/>
                          </svg>
                        </button>
                        {actionDropdown.show && actionDropdown.projectId === project.id && (
                          <div className="pm-action-menu">
                            <div className="pm-action-item" onClick={() => navigate(`/admin/projects/${project.id}`)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#333" strokeWidth="2"/><circle cx="12" cy="12" r="3" stroke="#333" strokeWidth="2"/></svg>
                              View
                            </div>
                            <div className="pm-action-item" onClick={() => navigate(`/project-input?edit=${project.id}`)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              Edit
                            </div>
                            {['draft', 'pending_approval', 'rejected'].includes(project.status?.toLowerCase()) && (
                              <div className="pm-action-item" onClick={() => handleStatusChange(project.id, 'approved')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="22 4 12 14.01 9 11.01" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                Approve
                              </div>
                            )}
                            {['approved', 'unpublished'].includes(project.status?.toLowerCase()) && (
                              <div className="pm-action-item" onClick={() => handleStatusChange(project.id, 'published')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="22 4 12 14.01 9 11.01" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                Publish
                              </div>
                            )}
                            {project.status?.toLowerCase() === 'published' && (
                              <div className="pm-action-item" onClick={() => handleStatusChange(project.id, 'unpublished')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><line x1="1" y1="1" x2="23" y2="23" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                Unpublish
                              </div>
                            )}
                            {!['draft'].includes(project.status?.toLowerCase()) && (
                              <div className="pm-action-item" onClick={() => handleStatusChange(project.id, 'draft')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#f59e0b" strokeWidth="2"/><polyline points="12 6 12 12 16 14" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                Set Draft
                              </div>
                            )}
                            {!['completed'].includes(project.status?.toLowerCase()) && (
                              <div className="pm-action-item" onClick={() => handleStatusChange(project.id, 'completed')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="22 4 12 14.01 9 11.01" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                Complete
                              </div>
                            )}
                            <div className="pm-action-item" onClick={() => handleNotesModal(project)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="14 2 14 8 20 8" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><line x1="16" y1="13" x2="8" y2="13" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><line x1="16" y1="17" x2="8" y2="17" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              Notes
                            </div>
                            <div className="pm-action-item pm-action-delete" onClick={() => handleDeleteProject(project.id)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><polyline points="3 6 5 6 21 6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              Delete
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="pm-no-data">No projects found</div>
              )}
            </div>
          </div>
          )}

          {/* Pagination */}
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      {/* Notes Modal */}
      {showNotesModal && (
        <div className="pm-notes-modal-overlay" onClick={() => setShowNotesModal(false)}>
          <div className="pm-notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pm-notes-modal-header">
              <h3>Notes for {notesProject?.title || 'Project'}</h3>
              <button className="pm-notes-close-btn" onClick={() => setShowNotesModal(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="pm-notes-modal-body">
              <textarea
                className="pm-notes-textarea"
                value={projectNotes}
                onChange={(e) => setProjectNotes(e.target.value)}
                placeholder="Enter notes about this project..."
                rows={6}
                readOnly
              />
            </div>
            <div className="pm-notes-modal-footer">
              <button className="pm-notes-cancel-btn" onClick={() => setShowNotesModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="admin-create-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="admin-create-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-create-modal-header">
              <h3>Add New Project</h3>
              <button className="admin-create-modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="admin-create-modal-body">
              <div className="admin-create-form-group">
                <label>Project Title <span className="required">*</span></label>
                <input value={createForm.title} onChange={e => setCreateForm({...createForm, title: e.target.value})} placeholder="Enter project title" />
                {createErrors.title && <div className="admin-create-form-error">{createErrors.title}</div>}
              </div>
              <div className="admin-create-form-group">
                <label>Description</label>
                <textarea value={createForm.description} onChange={e => setCreateForm({...createForm, description: e.target.value})} placeholder="Project description" />
              </div>
              <div className="admin-create-form-row">
                <div className="admin-create-form-group">
                  <label>Project Type</label>
                  <select value={createForm.projectType} onChange={e => setCreateForm({...createForm, projectType: e.target.value})}>
                    <option value="">Select type</option>
                    <option value="Residential">Residential</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Industrial">Industrial</option>
                    <option value="Infrastructure">Infrastructure</option>
                    <option value="Mixed Use">Mixed Use</option>
                  </select>
                </div>
                <div className="admin-create-form-group">
                  <label>Status</label>
                  <select value={createForm.status} onChange={e => setCreateForm({...createForm, status: e.target.value})}>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="hidden">Hidden</option>
                  </select>
                </div>
              </div>
              <div className="admin-create-form-row">
                <div className="admin-create-form-group">
                  <label>Budget</label>
                  <input type="number" value={createForm.budget} onChange={e => setCreateForm({...createForm, budget: e.target.value})} placeholder="Budget amount" />
                </div>
                <div className="admin-create-form-group">
                  <label>Currency</label>
                  <select value={createForm.currency} onChange={e => setCreateForm({...createForm, currency: e.target.value})}>
                    {currencies.map(c => (
                      <option key={c.code} value={c.code}>{c.code}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="admin-create-form-row">
                <div className="admin-create-form-group">
                  <label>Country</label>
                  <input value={createForm.country} onChange={e => setCreateForm({...createForm, country: e.target.value})} placeholder="Country" />
                </div>
                <div className="admin-create-form-group">
                  <label>City</label>
                  <input value={createForm.city} onChange={e => setCreateForm({...createForm, city: e.target.value})} placeholder="City" />
                </div>
              </div>
              <div className="admin-create-form-group">
                <label>Address</label>
                <input value={createForm.address} onChange={e => setCreateForm({...createForm, address: e.target.value})} placeholder="Full address" />
              </div>
              <div className="admin-create-form-group">
                <label>Company Name</label>
                <input value={createForm.companyName} onChange={e => setCreateForm({...createForm, companyName: e.target.value})} placeholder="Company name" />
              </div>
              <div className="admin-create-form-row">
                <div className="admin-create-form-group">
                  <label>Contact Name</label>
                  <input value={createForm.contactName} onChange={e => setCreateForm({...createForm, contactName: e.target.value})} placeholder="Contact person" />
                </div>
                <div className="admin-create-form-group">
                  <label>Contact Email</label>
                  <input type="email" value={createForm.contactEmail} onChange={e => setCreateForm({...createForm, contactEmail: e.target.value})} placeholder="Contact email" />
                </div>
              </div>
              <div className="admin-create-form-group">
                <label>Contact Phone</label>
                <input value={createForm.contactPhone} onChange={e => setCreateForm({...createForm, contactPhone: e.target.value})} placeholder="Contact phone" />
              </div>
              {createErrors.submit && <div className="admin-create-form-error">{createErrors.submit}</div>}
            </div>
            <div className="admin-create-modal-footer">
              <button className="admin-create-cancel-btn" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="admin-create-save-btn" onClick={handleCreateProject} disabled={createSaving}>{createSaving ? 'Creating...' : 'Create Project'}</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ProjectManagePage;
